import { useAtomSet } from "@effect/atom-react";
import { P, usePermission } from "@crm/access/authorization/client";
import { useAuth } from "@crm/access/hooks/use-auth";
import { signOutAndLeaveWorkbench } from "../lib/account-session";
import { reactivityInvalidateAtom } from "@crm/shared/query-atom";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  BookOpen,
  BookOpenCheck,
  ClipboardList,
  Globe,
  LayoutGrid,
  Library,
  LogOut,
  Megaphone,
  MessageSquare,
  Settings,
  Upload,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { useMyCapabilities } from "#app/sites";
import { SiteSwitcher } from "./site-switcher";

/**
 * Workbench 统一外壳（redesign v2）：侧栏 + 顶栏 + 内容区。
 * 导航可见性权限驱动（课程上传 = review-course:write:all），无角色特例代码。
 */

type NavItem = {
  readonly to: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly match: string; // pathname startsWith
  /** 课程能力门禁（声明见 @crm/domain/capabilities）；不设则全员可见 */
  readonly capability?: "site-tools" | "sio-tools" | "design-templates";
};

type NavSection = {
  readonly title: string;
  readonly items: readonly NavItem[];
};

const LEARN_SECTION: NavSection = {
  title: "我的课",
  items: [
    { to: "/", label: "首页", icon: <LayoutGrid size={17} />, match: "/" },
    {
      to: "/student-tasks",
      label: "我的作业",
      icon: <ClipboardList size={17} />,
      match: "/student-tasks",
    },
    {
      to: "/student-feedback",
      label: "课程反馈",
      icon: <MessageSquare size={17} />,
      match: "/student-feedback",
    },
    {
      to: "/review-course",
      label: "复习视频",
      icon: <BookOpen size={17} />,
      match: "/review-course",
    },
    {
      to: "/knowledge",
      label: "知识库",
      icon: <Library size={17} />,
      match: "/knowledge",
    },
    // 设计参考已迁入建站工具（/site-tools/references），不再单列
    {
      to: "/operation-guide",
      label: "操作说明",
      icon: <BookOpenCheck size={17} />,
      match: "/operation-guide",
    },
  ],
};

const TOOLS_SECTION: NavSection = {
  title: "工具",
  items: [
    {
      to: "/site-tools",
      label: "建站工具",
      icon: <Globe size={17} />,
      match: "/site-tools",
      capability: "site-tools",
    },
    {
      to: "/sio-tools",
      label: "推广工具",
      icon: <Megaphone size={17} />,
      match: "/sio-tools",
      capability: "sio-tools",
    },
  ],
};

const PAGE_TITLES: ReadonlyArray<readonly [string, string]> = [
  ["/operation-guide", "操作说明"],
  ["/student-tasks", "我的作业"],
  ["/student-feedback", "课程反馈"],
  ["/review-course/admin", "课程管理"],
  ["/review-course", "复习视频"],
  ["/knowledge", "知识库"],
  ["/site-tools/references", "设计参考"],
  ["/site-tools", "建站工具"],
  ["/sio-tools", "推广工具"],
  ["/sites", "我的站点"],
];

const ROLE_LABELS: Record<string, string> = {
  student: "学员",
  admin: "管理员",
  teacher: "讲师",
  coach: "教练",
  "coach-manager": "教练主管",
  reviewer: "审核",
  sales: "销售",
};

function pageTitle(pathname: string): string {
  for (const [prefix, title] of PAGE_TITLES) {
    if (pathname.startsWith(prefix)) {
      return title;
    }
  }
  return "首页";
}

function NavSectionView({
  section,
  capabilities,
}: {
  section: NavSection;
  capabilities: Set<string>;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const visibleItems = section.items.filter(
    (item) => !item.capability || capabilities.has(item.capability),
  );
  if (visibleItems.length === 0) {
    return null;
  }
  return (
    <>
      <div className="px-2.5 pt-4 pb-1.5 font-semibold text-[11px] text-ink-3 uppercase tracking-widest first:pt-0.5">
        {section.title}
      </div>
      {visibleItems.map((item) => {
        const active = item.match === "/" ? pathname === "/" : pathname.startsWith(item.match);
        return (
          <Link
            className={`relative mx-0 my-0.5 flex items-center gap-2.5 rounded-[9px] px-2.5 py-2 font-medium text-[13.5px] transition-colors ${
              active
                ? "bg-brand-soft font-semibold text-brand-text"
                : "text-ink-2 hover:bg-muted hover:text-ink"
            }`}
            key={item.to}
            to={item.to}
          >
            {active ? (
              <span className="-left-3 absolute top-[20%] h-[60%] w-[3px] rounded-r bg-brand" />
            ) : null}
            {item.icon}
            {item.label}
          </Link>
        );
      })}
    </>
  );
}

export function WorkbenchShell({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const canManageCourses = usePermission(P.REVIEW_COURSE_WRITE_ALL);
  const {
    capabilities,
    isLoading: capabilitiesLoading,
    isFailure: capabilitiesFailure,
  } = useMyCapabilities(user?.id);
  const invalidateCapabilities = useAtomSet(reactivityInvalidateAtom);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const aiSettingsPath = capabilities.has("sio-tools")
    ? "/sio-tools/settings"
    : "/site-tools/settings";
  const canConfigureAi = capabilities.has("sio-tools") || capabilities.has("site-tools");

  const handleLogout = () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);
    void signOutAndLeaveWorkbench().catch(() => {
      setIsLoggingOut(false);
      toast.error("退出登录失败，请重试");
    });
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* ---- Sidebar ---- */}
      <aside className="sticky top-0 flex h-screen w-[232px] shrink-0 flex-col border-border border-r bg-card">
        <div className="flex items-baseline gap-1.5 border-border border-b px-5 pt-[22px] pb-[18px]">
          <span className="flex h-[26px] w-[26px] translate-y-1 items-center justify-center rounded-lg bg-gradient-to-br from-brand to-violet font-bold font-display text-sm text-white">
            i
          </span>
          <span className="font-bold text-base tracking-tight">imiker</span>
          <span className="font-medium text-[11.5px] text-ink-3">学员工作台</span>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-3.5">
          <NavSectionView capabilities={capabilities} section={LEARN_SECTION} />
          {canManageCourses ? (
            <NavSectionView
              capabilities={capabilities}
              section={{
                title: "内容管理 · 员工",
                items: [
                  {
                    to: "/review-course/admin",
                    label: "课程上传",
                    icon: <Upload size={17} />,
                    match: "/review-course/admin",
                  },
                ],
              }}
            />
          ) : null}
          {capabilitiesLoading ? (
            <div className="px-2.5 pt-4 text-[12px] text-ink-3">工具权限加载中…</div>
          ) : capabilitiesFailure ? (
            <button
              className="mx-0 mt-3 w-full rounded-[9px] border border-border px-2.5 py-2 text-left text-[12px] text-ink-2 hover:bg-muted"
              onClick={() => invalidateCapabilities(["workbenchSite"])}
              type="button"
            >
              工具权限加载失败，点击重试
            </button>
          ) : (
            <NavSectionView capabilities={capabilities} section={TOOLS_SECTION} />
          )}
        </nav>
        <div className="border-border border-t p-3">
          <div className="flex items-center gap-2.5 rounded-[10px] px-2.5 py-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-500 to-amber-600 font-semibold text-[13px] text-white">
              {user?.name?.slice(0, 1) ?? "?"}
            </span>
            <div className="min-w-0">
              <div className="truncate font-semibold text-[13px]">{user?.name ?? "未登录"}</div>
              <div className="text-[11.5px] text-ink-3">
                {ROLE_LABELS[user?.role ?? ""] ?? user?.role ?? ""}
              </div>
            </div>
          </div>
          {canConfigureAi ? (
            <Link
              className="mt-1 flex items-center gap-2.5 rounded-[9px] px-2.5 py-2 font-medium text-[13px] text-ink-2 transition-colors hover:bg-muted hover:text-ink"
              to={aiSettingsPath}
            >
              <Settings size={16} />
              AI API 设置
            </Link>
          ) : null}
          <button
            aria-label="退出登录"
            className="mt-1 flex w-full items-center justify-center gap-2 rounded-[9px] px-2.5 py-2 font-medium text-[13px] text-ink-2 transition-colors hover:bg-muted hover:text-ink disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isLoggingOut}
            onClick={handleLogout}
            type="button"
          >
            <LogOut size={16} />
            {isLoggingOut ? "退出中…" : "退出登录"}
          </button>
        </div>
      </aside>

      {/* ---- Main ---- */}
      <main className="min-w-0 flex-1">
        <div className="flex items-center gap-3.5 px-9 pt-4">
          <span className="font-medium text-[13px] text-ink-3">{pageTitle(pathname)}</span>
          <span className="flex-1" />
          <SiteSwitcher />
        </div>
        {children}
      </main>
    </div>
  );
}
